/**
 * Safe formatting utilities that handle undefined/null/NaN/Infinity gracefully.
 *
 * Use these instead of calling .toLocaleString(), .toFixed(), .toLocaleDateString(),
 * or .toISOString() directly on values that may be nullish or non-finite.
 */

export function formatNumber(
  value: unknown,
  fallback = "0",
  locales?: Intl.LocalesArgument,
  options?: Intl.NumberFormatOptions
): string {
  return typeof value === "number" && Number.isFinite(value)
    ? value.toLocaleString(locales, options)
    : fallback;
}

export function formatFixed(
  value: unknown,
  digits = 0,
  fallback = "0"
): string {
  return typeof value === "number" && Number.isFinite(value)
    ? value.toFixed(digits)
    : fallback;
}

export function formatPercent(
  value: unknown,
  maximumFractionDigits = 1,
  fallback = "0%"
): string {
  return typeof value === "number" && Number.isFinite(value)
    ? `${value.toLocaleString(undefined, { maximumFractionDigits })}%`
    : fallback;
}

function coerceDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

export function formatDate(
  value: unknown,
  fallback = "-",
  locales?: Intl.LocalesArgument,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = coerceDate(value);
  return date ? date.toLocaleDateString(locales, options) : fallback;
}

export function formatDateTime(
  value: unknown,
  fallback = "-",
  locales?: Intl.LocalesArgument,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = coerceDate(value);
  return date ? date.toLocaleString(locales, options) : fallback;
}

export function formatTime(
  value: unknown,
  fallback = "-",
  locales?: Intl.LocalesArgument,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = coerceDate(value);
  return date ? date.toLocaleTimeString(locales, options) : fallback;
}
